<template>
  <Teleport to="body">
    <!-- The backdrop does not dismiss: a stray click outside would throw away a
         half-filled pick and its comment. Escape and the close button do. -->
    <div v-if="open" class="tc-cell-detail-backdrop">
      <div
        class="tc-attribution-card tc-wizard"
        role="dialog"
        aria-label="Attribution wizard"
        data-testid="attribution-wizard"
        @click.stop
      >
        <div class="tc-attribution-header tc-wizard-header">
          <div class="tc-wizard-heading">
            <h2 class="tc-attribution-event-title tc-wizard-title">
              <span class="tc-wizard-title-text">{{ contextLabel }}</span>
              <SourceLink :link="link ?? null" />
            </h2>
            <!-- What the source knows about the item: the strip the suggestions
                 below get judged against (Items 7 + 12). -->
            <SourceFactsRow :facts="facts ?? null" />
            <!-- Where the time being re-targeted came from: one line per event,
                 each keeping its own address in the source. The count leads, so
                 a list longer than the scroll cap doesn't read as the whole of
                 it. -->
            <template v-if="events.length > 0">
              <div class="tc-wizard-sources-count" data-testid="wizard-source-count">
                {{ events.length }} {{ events.length === 1 ? 'event' : 'events' }}
              </div>
              <ul class="tc-wizard-sources tc-scroll" data-testid="wizard-source-events">
                <li v-for="ev in events" :key="ev.eventId" class="tc-wizard-source">
                  <SourceBadge v-if="ev.source" :source="ev.source" variant="compact" />
                  <span class="tc-wizard-source-when">{{ ev.start ? formatWhen(ev.start) : '' }}</span>
                  <span class="tc-wizard-source-title">{{ ev.subject ?? 'Event details unavailable' }}</span>
                  <SourceLink :link="ev.link ?? null" />
                  <span class="tc-wizard-source-hours">{{ ev.hours.toFixed(2) }}h</span>
                </li>
              </ul>
            </template>
          </div>
          <button
            type="button"
            class="tc-btn-icon tc-wizard-close"
            aria-label="Cancel"
            data-testid="wizard-cancel-x"
            @click="emit('cancel')"
          >
            <svg viewBox="0 0 18 18" fill="none" aria-hidden="true" class="tc-cell-picker-x">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="tc-attribution-body tc-wizard-body tc-scroll">
          <!-- Three panes side by side (VP-style): Project → Phase → Task. -->
          <input
            type="search"
            class="form-control form-control-sm tc-wizard-search tc-wizard-search--horizontal"
            placeholder="Search projects…"
            autocomplete="off"
            data-testid="wizard-project-search"
            :value="query"
            @input="onQueryInput"
          />
          <div class="tc-wizard-panes">
            <div class="tc-wizard-pane" :class="{ 'border-danger': flagged('project') }" data-testid="wizard-project-step">
              <div class="tc-wizard-pane-head">Project <span class="tc-wizard-step-req" :class="{ ok: !!selectedProject }">{{ selectedProject ? '✓' : 'required' }}</span></div>
              <div v-if="projectLoading" class="tc-wizard-pane-list tc-picker-loading" data-testid="wizard-project-loading">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </div>
              <ul v-else class="tc-wizard-options tc-wizard-pane-list">
                <template v-for="(opt, i) in projectResults" :key="opt.id">
                  <!-- Resolve mode groups the idle list: the item's candidates
                       under "Suggested", the user's recents under "Recent". A
                       heading leads each run; unset elsewhere, so the list is flat. -->
                  <li
                    v-if="opt.group && opt.group !== projectResults[i - 1]?.group"
                    class="tc-wizard-optgroup"
                    role="presentation"
                    data-testid="wizard-project-group"
                  >{{ opt.group === 'suggested' ? 'Suggested' : 'Recent' }}</li>
                  <li
                    class="tc-wizard-option"
                    :class="{ 'tc-wizard-option--sel': projectModel?.id === opt.id }"
                    data-testid="wizard-project-option"
                    @click="onProjectSelect(opt)"
                  >
                    <!-- A missing project name falls back to its WBS code so the
                         row is never a blank line; the code column then drops out. -->
                    <span>{{ opt.name || opt.code }}</span>
                    <span v-if="opt.name && opt.code" class="tc-wizard-option-code txt-mono">{{ opt.code }}</span>
                  </li>
                </template>
                <li v-if="projectResults.length === 0" class="tc-wizard-option-empty">{{ projectEmpty }}</li>
              </ul>
            </div>

            <div class="tc-wizard-pane" :class="{ 'border-danger': flagged('phase') }" data-testid="wizard-phase-step">
              <div class="tc-wizard-pane-head">Phase <span class="tc-wizard-step-req" :class="{ ok: phaseStepSatisfied }">{{ phaseStepLabel }}</span></div>
              <div v-if="phasesLoading" class="tc-wizard-pane-list tc-picker-loading" data-testid="wizard-phase-loading">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </div>
              <ul v-else-if="showPhase" class="tc-wizard-options tc-wizard-pane-list">
                <li
                  v-for="p in phaseCandidates"
                  :key="p.wbs2"
                  class="tc-wizard-option"
                  :class="{ 'tc-wizard-option--sel': selectedPhase?.wbs2 === p.wbs2 }"
                  data-testid="wizard-phase-option"
                  @click="onPhaseChange(p)"
                >
                  <span>{{ p.name || p.wbs2 }}</span>
                  <span v-if="p.name" class="tc-wizard-option-code txt-mono">{{ p.wbs2 }}</span>
                </li>
              </ul>
              <div v-else-if="phasesUnknown" class="tc-wizard-pane-empty" data-testid="wizard-phase-unknown">
                Couldn't load phases
                <button type="button" class="btn btn-link btn-sm p-0" data-testid="wizard-phase-retry" @click="refetchPhases()">Retry</button>
              </div>
              <div v-else class="tc-wizard-pane-empty">{{ phasePaneHint }}</div>
            </div>

            <div class="tc-wizard-pane" :class="{ 'border-danger': flagged('task') }" data-testid="wizard-task-step">
              <div class="tc-wizard-pane-head">Task <span class="tc-wizard-step-req" :class="{ ok: taskStepSatisfied }">{{ taskStepLabel }}</span></div>
              <div v-if="taskLoading" class="tc-wizard-pane-list tc-picker-loading" data-testid="wizard-task-loading">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </div>
              <ul v-else-if="showTask" class="tc-wizard-options tc-wizard-pane-list">
                <li
                  v-for="t in taskCandidates"
                  :key="t.wbs3"
                  class="tc-wizard-option"
                  :class="{ 'tc-wizard-option--sel': selectedTask?.wbs3 === t.wbs3 }"
                  data-testid="wizard-task-option"
                  @click="selectedTask = t"
                >
                  <span>{{ t.name || t.wbs3 }}</span>
                  <span v-if="t.name" class="tc-wizard-option-code txt-mono">{{ t.wbs3 }}</span>
                </li>
              </ul>
              <!-- VP could not be asked. Saying "No task" here would be a
                   fabricated answer, and picking the phase as the leaf would
                   produce a save VP rejects. -->
              <div v-else-if="tasksUnknown" class="tc-wizard-pane-empty" data-testid="wizard-task-unknown">
                Couldn't load tasks
                <button type="button" class="btn btn-link btn-sm p-0" data-testid="wizard-task-retry" @click="refetchTasks()">Retry</button>
              </div>
              <!-- A leaf phase has no task to pick — the pane names that, mirroring
                   a flat project's phase pane. -->
              <div v-else class="tc-wizard-pane-empty">{{ taskPaneHint }}</div>
            </div>
          </div>
        </div>

        <!-- Footer: Day / Hours / Comment / Confirm stay out of the scroll region
             above so they're always reachable however long the option lists get. -->
        <div class="tc-wizard-footer">
          <div class="tc-wizard-footer-fields">
            <!-- Day + Hours (create mode only — a new row has no source event) -->
            <div v-if="mode === 'create'" class="tc-wizard-create-fields">
              <div v-if="!repeat" class="tc-attribution-field flex-fill mb-0">
                <label class="tc-attribution-label" for="wizard-day">Day</label>
                <select id="wizard-day" class="form-select form-select-sm" data-testid="wizard-day" v-model.number="dayIndex">
                  <option v-for="d in dayOptions" :key="d.index" :value="d.index">{{ d.label }}</option>
                </select>
              </div>
              <div class="tc-attribution-field mb-0">
                <label class="tc-attribution-label" for="wizard-hours">Hours</label>
                <input id="wizard-hours" type="number" :step="hoursIncrement" min="0" max="24" class="form-control form-control-sm tc-wizard-hours" :class="{ 'is-invalid': flagged('hours') }" data-testid="wizard-hours" v-model.number="hours" />
              </div>
            </div>

            <!-- Repeat: the same hours on every working day of the period. The
                 day strip mirrors the grid's columns above, so the days being
                 booked are read in the shape they are already being looked at. -->
            <div v-if="mode === 'create'" class="form-check mb-0">
              <input id="wizard-repeat" class="form-check-input" type="checkbox" data-testid="wizard-repeat" v-model="repeat" />
              <label class="form-check-label" for="wizard-repeat">Repeat</label>
            </div>

            <div v-if="mode === 'create' && repeat" class="tc-wizard-repeat-strip-scroll tc-scroll">
              <div class="tc-wizard-repeat-strip" :class="{ 'border border-danger rounded p-2': flagged('days') }" role="group" aria-label="Days to repeat on" data-testid="wizard-repeat-days">
                <div v-for="g in dayGroups" :key="g.month" class="tc-wizard-repeat-month">
                  <span class="tc-wizard-repeat-month-label" aria-hidden="true">{{ g.month }}</span>
                  <button
                    v-for="d in g.days"
                    :key="d.index"
                    type="button"
                    class="tc-wizard-repeat-day"
                    :class="{ 'tc-wizard-repeat-day--non-working': d.isNonWorking }"
                    data-testid="wizard-repeat-day"
                    :aria-pressed="repeatDays.includes(d.index)"
                    :aria-label="d.label"
                    @click="toggleRepeatDay(d.index)"
                  >
                    <span class="tc-wizard-repeat-day-dow">{{ d.dow }}</span>
                    <span class="tc-wizard-repeat-day-num txt-mono">{{ d.dayOfMonth }}</span>
                    <span class="tc-wizard-repeat-day-tick"></span>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="mode === 'create' && repeat" class="tc-wizard-repeat-preview" data-testid="wizard-repeat-preview">{{ repeatPreview }}</div>

            <!-- Comment — required for create/resolve. Edit mode omits it: the
                 comment is per-day-cell (edited in the cell card), so a single
                 row-level comment here would clobber distinct cell notes. -->
            <div v-if="mode !== 'edit'" class="tc-wizard-comment">
              <div class="tc-wizard-step-label">Comment <span class="tc-wizard-step-req" :class="{ ok: comment.trim().length > 0 }">{{ comment.trim().length > 0 ? '✓' : 'required' }}</span></div>
              <textarea
                class="form-control tc-cell-detail-note-input"
                :class="{ 'is-invalid': flagged('comment') }"
                rows="2"
                placeholder=""
                data-testid="wizard-comment"
                v-model="comment"
              ></textarea>
              <div v-if="commentHint" class="tc-wizard-comment-hint" data-testid="wizard-comment-hint">{{ commentHint }}</div>
            </div>
          </div>

          <div class="tc-attribution-action-bar">
            <div class="tc-attribution-action-spacer"></div>
            <span
              v-if="blockerShown && blocker"
              class="text-danger small me-auto"
              role="alert"
              data-testid="wizard-blocker"
            >{{ blocker.message }}</span>
            <button type="button" class="btn btn-outline-secondary btn-sm" data-testid="wizard-cancel" @click="emit('cancel')">Cancel</button>
            <button type="button" class="btn btn-primary btn-sm" data-testid="wizard-confirm" @click="onConfirm">Confirm</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { usePhaseCascade } from '../composables/usePhaseCascade';
import { formatMonthDay, periodDayCount } from '../utils/period';
import { useProjectSearch } from '../composables/useProjectSearch';
import type { ComboOption } from './SearchCombobox.vue';
import type { BillingTarget } from '../api/draft';
import type { TimesheetRow } from '../api/client';
import type { SourceEventRef, SourceFacts, SourceLink as SourceLinkModel } from './sourceFacts';
import SourceBadge from './ui/SourceBadge.vue';
import SourceFactsRow from './ui/SourceFactsRow.vue';
import SourceLink from './ui/SourceLink.vue';

// One progressive picker for both creating a manual row and resolving a
// needs-input item (ADR-0042). Three VP-style panes: Project → Phase → Task,
// with a Comment in the footer. Phase appears once a project is chosen, task
// once a phase is; a field is required when it has candidates; the comment is
// always required.
const props = withDefaults(defineProps<{
  open: boolean;
  // 'create' opens for a brand-new row (Day + Hours are collected here);
  // 'resolve' opens for a needs-input item (its day/hours are fixed upstream);
  // 'edit' re-targets an existing row (pre-seeded; no day/hours/comment — the
  // comment stays per-cell, edited in the cell card).
  mode: 'create' | 'resolve' | 'edit';
  contextLabel: string;
  periodStart?: string;
  periodEnd?: string;
  timesheetRows?: TimesheetRow[];
  recent?: BillingTarget[];
  // Resolve mode: the item's candidate targets, shown under a "Suggested"
  // heading above recents. Empty in create/edit.
  suggested?: BillingTarget[];
  rankedTargetIds?: string[];
  categories?: BillingTarget[];
  hoursIncrement?: number;
  // Edit mode: the row's current target, pre-seeding project → phase → task.
  initialTarget?: BillingTarget | null;
  // Resolve mode: the carried note pre-filled into the comment field, and where
  // it came from ("From the ticket note") when that isn't the agent.
  initialComment?: string;
  commentHint?: string;
  // Resolve mode: what the source knows about the item, shown under the title,
  // and where the item lives in the source, shown beside it.
  facts?: SourceFacts | null;
  link?: SourceLinkModel | null;
  // Edit mode: the events the row's time came from, listed under the title so a
  // re-target can be checked against its own sources.
  events?: SourceEventRef[];
  // Create mode: the weekdays VP does not work, as Date.getDay() numbers. A
  // repeat covers the rest; a non-working day is still tickable by hand.
  nonWorkingDays?: number[];
}>(), {
  periodStart: '',
  periodEnd: '',
  timesheetRows: () => [],
  recent: () => [],
  suggested: () => [],
  rankedTargetIds: () => [],
  categories: () => [],
  hoursIncrement: 0.25,
  initialTarget: null,
  initialComment: '',
  commentHint: '',
  facts: null,
  link: null,
  events: () => [],
  nonWorkingDays: () => [0, 6],
});

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const emit = defineEmits<{
  // Create mode carries every day the entry lands on — one for a single day,
  // the picked set for a repeat.
  confirm: [target: BillingTarget, comment: string, entry?: { dayIndexes: number[]; hours: number }];
  cancel: [];
}>();

// ── Selection state ───────────────────────────────────────────────────────────
interface SelectedProject { wbs1: string; wbs1Name: string; category?: string }
const selectedProject = ref<SelectedProject | null>(null);
const comment = ref(props.initialComment);
const dayIndex = ref(0);
const hours = ref<number>(1);
const repeat = ref(false);
const repeatDays = ref<number[]>([]);

// ── Project search (shared combobox) ────────────────────────────────────────
const {
  query,
  options: projectResults,
  loading: projectLoading,
  emptyLabel: projectEmpty,
  setQuery: setProjectQuery,
  resolve: resolveProject,
  reset: resetProjectSearch,
} = useProjectSearch({
  recent: () => props.recent,
  suggested: () => props.suggested,
  rankedTargetIds: () => props.rankedTargetIds,
  enabled: () => props.open,
  timesheetRows: () => props.timesheetRows ?? [],
  categories: () => props.categories,
});

// The Project pane's search box feeds the shared project query.
function onQueryInput(e: Event): void {
  setProjectQuery((e.target as HTMLInputElement).value);
}

const projectModel = computed((): ComboOption | null => {
  const p = selectedProject.value;
  if (!p) return null;
  // A category is name-only with a `cat:` option id; a project shows its WBS code.
  return p.category
    ? { id: `cat:${p.category}`, name: p.wbs1Name || p.wbs1 }
    : { id: p.wbs1, code: p.wbs1, name: p.wbs1Name };
});

// ── Phase / Task cascade (shared logic) ────────────────────────────────────────
// Ask VP whether the project is phased rather than trusting the option's own
// flag. A suggested candidate carries no isPhased — BillingTarget has no such
// field — so useProjectSearch infers it from "the candidate already has a
// wbs2, or the sheet already has a phased row for it". Both are false for a
// phased project new to this sheet, and gating the lookup on that guess meant
// the phase pane read "No phase" while VP held six, and the resulting
// phase-less row was then rejected at save. Categories have no phases.
const cascadeEnabled = computed(() =>
  props.open && !!selectedProject.value?.wbs1 && !selectedProject.value?.category);

const {
  selectedPhase,
  selectedTask,
  phaseCandidates,
  taskCandidates,
  phasesLoading,
  tasksLoading,
  phasesUnknown,
  tasksUnknown,
  refetchPhases,
  refetchTasks,
  phaseHasTasks,
  leafChosen,
  onPhaseChange,
  reset: resetCascade,
} = usePhaseCascade({
  wbs1: () => selectedProject.value?.wbs1 ?? '',
  timesheetRows: () => props.timesheetRows ?? [],
  enabled: () => cascadeEnabled.value,
});

// Phase step appears once VP has returned phases for the chosen project; task
// step once the chosen phase has tasks. A genuinely unphased project returns
// none and shows neither. An errored lookup is phasesUnknown, not "none" — a
// failed read must never read as an empty one (#6238).
const showPhase = computed(() => phaseCandidates.value.length > 0);
const showTask = computed(() => showPhase.value && !!selectedPhase.value && phaseHasTasks.value);
const taskLoading = computed(() => showPhase.value && !!selectedPhase.value && tasksLoading.value);

// Horizontal-pane copy: guide the user forward while a level is still pending,
// then name the level VP doesn't have (a flat project has no phase; a leaf phase
// has no task) so a satisfied pane never reads as an empty one.
const phasePaneHint = computed(() => (selectedProject.value ? 'No phase' : 'Choose a project'));
const taskPaneHint = computed(() => {
  if (!selectedProject.value) return '';
  return showPhase.value && !selectedPhase.value ? 'Choose a phase' : 'No task';
});

// An unloaded list is neither satisfied nor a known requirement.
const phaseStepSatisfied = computed(() => !phasesUnknown.value && (!showPhase.value || !!selectedPhase.value));
const phaseStepLabel = computed(() => {
  if (phasesUnknown.value) return '?';
  if (!showPhase.value) return '\u2014';
  return selectedPhase.value ? '\u2713' : 'required';
});


const taskStepSatisfied = computed(() => !tasksUnknown.value && (!showTask.value || !!selectedTask.value));
const taskStepLabel = computed(() => {
  if (tasksUnknown.value) return '?';
  if (!showTask.value) return '\u2014';
  return selectedTask.value ? '\u2713' : 'required';
});

function onProjectSelect(opt: ComboOption): void {
  const p = resolveProject(opt.id);
  if (!p) return;
  selectedProject.value = { wbs1: p.wbs1, wbs1Name: p.wbs1Name, category: p.category };
  // A recent may already carry a resolved leaf — pre-seed it so a repeat pick of
  // a phased favourite is one click.
  resetCascade(
    p.recent?.wbs2 ? { wbs2: p.recent.wbs2, name: p.recent.wbs2Name, chargeType: '', status: 'A' } : null,
    p.recent?.wbs3 ? { wbs3: p.recent.wbs3, name: p.recent.wbs3Name, chargeType: '', status: 'A' } : null,
  );
}

// ── Day options (create mode) ───────────────────────────────────────────────
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayOptions = computed(() => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(props.periodStart);
  const nonWorking = new Set(props.nonWorkingDays);
  return Array.from({ length: periodDayCount(props.periodStart, props.periodEnd) }, (_, i) => {
    if (!m) return { index: i, label: `Day ${i + 1}`, month: '', dow: '', dayOfMonth: i + 1, isNonWorking: false };
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    d.setUTCDate(d.getUTCDate() + i);
    return {
      index: i,
      label: `${DOW[d.getUTCDay()]} ${formatMonthDay(d.toISOString().slice(0, 10))}`,
      month: MON[d.getUTCMonth()],
      dow: DOW[d.getUTCDay()][0],
      dayOfMonth: d.getUTCDate(),
      isNonWorking: nonWorking.has(d.getUTCDay()),
    };
  });
});

// The strip leads each run of days with its month, the way the grid leads its
// columns with the project. One label for a period inside a month, two where it
// runs over the end.
const dayGroups = computed(() => {
  const groups: { month: string; days: typeof dayOptions.value }[] = [];
  for (const day of dayOptions.value) {
    const run = groups[groups.length - 1];
    if (run && run.month === day.month) run.days.push(day);
    else groups.push({ month: day.month, days: [day] });
  }
  return groups;
});

// ── Repeat (create mode) ────────────────────────────────────────────────────
const workingDayIndexes = computed(() => dayOptions.value.filter(d => !d.isNonWorking).map(d => d.index));

const pickedDays = computed(() => [...repeatDays.value].sort((a, b) => a - b));

function toggleRepeatDay(index: number): void {
  repeatDays.value = repeatDays.value.includes(index)
    ? repeatDays.value.filter(i => i !== index)
    : [...repeatDays.value, index];
}

const repeatPreview = computed(() => {
  const n = pickedDays.value.length;
  const each = Number.isFinite(hours.value) ? hours.value : 0;
  return `${n} ${n === 1 ? 'day' : 'days'} · ${each.toFixed(2)}h each · ${(n * each).toFixed(2)}h`;
});

// Turning the toggle on re-offers the whole working week; the picker is where
// exceptions are taken back out.
watch(repeat, (on) => {
  if (on) repeatDays.value = workingDayIndexes.value;
});

// ── Committable target + gate ───────────────────────────────────────────────
// VP's phase list decides, never the option's guess: the cascade comment above
// is why. Trusting the guess here dropped a phase the user had picked.
const target = computed((): BillingTarget | null => {
  const p = selectedProject.value;
  if (!p) return null;
  if (!showPhase.value) {
    return { wbs1: p.wbs1, wbs1Name: p.wbs1Name, wbs2: '', wbs2Name: '', wbs3: '', wbs3Name: '', ...(p.category ? { category: p.category } : {}) };
  }
  const phase = selectedPhase.value;
  if (!phase) return null;
  return {
    wbs1: p.wbs1,
    wbs1Name: p.wbs1Name,
    wbs2: phase.wbs2,
    wbs2Name: phase.name,
    wbs3: selectedTask.value?.wbs3 ?? '',
    wbs3Name: selectedTask.value?.name ?? '',
  };
});

// A phased project needs its full leaf resolved before it can commit; a
// non-phased one is complete at the WBS1.
const phaseComplete = computed((): boolean => {
  const p = selectedProject.value;
  if (!p) return false;
  if (p.category) return true;
  if (phasesLoading.value || phasesUnknown.value) return false;
  if (phaseCandidates.value.length === 0) return true;
  return leafChosen.value;
});

// What Confirm is still waiting on — the field and what to do about it — in the
// order the form reads, or null when nothing is. Confirm stays clickable and
// says this on click: a disabled button gives the user nothing to act on and
// sends them hunting for the reason.
const blocker = computed((): { field: string; message: string } | null => {
  if (!selectedProject.value) return { field: 'project', message: 'Pick a project.' };
  if (phasesLoading.value) return { field: 'phase', message: 'Still loading phases.' };
  if (!phaseComplete.value) {
    return phaseStepSatisfied.value
      ? { field: 'task', message: 'Pick a task.' }
      : { field: 'phase', message: 'Pick a phase.' };
  }
  if (props.mode !== 'edit' && comment.value.trim().length === 0) {
    return { field: 'comment', message: 'Add a comment.' };
  }
  if (props.mode === 'create' && !(Number.isFinite(hours.value) && hours.value > 0)) {
    return { field: 'hours', message: 'Enter hours above zero.' };
  }
  if (props.mode === 'create' && repeat.value && pickedDays.value.length === 0) {
    return { field: 'days', message: 'Pick at least one day to repeat on.' };
  }
  return null;
});

// Set by a Confirm that couldn't go through, so the reason appears on the
// attempt rather than nagging a form the user is still filling in.
const blockerShown = ref(false);
const flagged = (field: string): boolean => blockerShown.value && blocker.value?.field === field;

function onConfirm(): void {
  if (blocker.value || !target.value) {
    blockerShown.value = true;
    return;
  }
  emit(
    'confirm',
    target.value,
    comment.value.trim(),
    props.mode === 'create'
      ? { dayIndexes: repeat.value ? pickedDays.value : [dayIndex.value], hours: hours.value }
      : undefined,
  );
}

// Reset the form each time the wizard opens.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return;
  comment.value = props.initialComment;
  blockerShown.value = false;
  resetProjectSearch();
  dayIndex.value = 0;
  hours.value = 1;
  repeat.value = false;
  repeatDays.value = [];
  // Edit mode pre-seeds the row's current project + leaf so the user re-picks
  // from where the row stands; other modes open blank.
  const seed = props.mode === 'edit' ? props.initialTarget : null;
  if (seed) {
    selectedProject.value = { wbs1: seed.wbs1, wbs1Name: seed.wbs1Name, category: seed.category };
    resetCascade(
      seed.wbs2 ? { wbs2: seed.wbs2, name: seed.wbs2Name, chargeType: '', status: 'A' } : null,
      seed.wbs3 ? { wbs3: seed.wbs3, name: seed.wbs3Name, chargeType: '', status: 'A' } : null,
    );
  } else {
    selectedProject.value = null;
    resetCascade(null, null);
  }
});

// Escape cancels the wizard; the drawer suppresses Escape while this is open, so
// it never leaks to the Triage drawer behind.
function onEscape(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.open) emit('cancel');
}
onMounted(() => document.addEventListener('keydown', onEscape));
onUnmounted(() => document.removeEventListener('keydown', onEscape));
</script>
